import ServiceTrialIcons from '@/icons/ServiceTrialIcons'
import { CustomerJourneyNote } from '@/modules/Customer/types'
import { ServiceStatus } from '@/modules/Service/types'
import { formatDateWithFormatter } from '@/utils/function'
import { paths } from '@/utils/path'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'

interface POCServiceCardProps {
    note: CustomerJourneyNote
    isLocked: boolean
    isCompleted: boolean
    isConversionPending: boolean
    onUpdateResult: (note: CustomerJourneyNote) => void
    onRetention: (note: CustomerJourneyNote) => void
    onConvert: (note: CustomerJourneyNote) => void
}

// Helper function to check POC expiry status
const getPocExpiryStatus = (endDate: string | null | undefined) => {
    if (!endDate?.trim())
        return { status: 'ongoing', daysLeft: null, isWarning: false }

    const now = dayjs()
    const stopDate = dayjs(endDate)
    if (!stopDate.isValid())
        return { status: 'ongoing', daysLeft: null, isWarning: false }

    const daysLeft = stopDate.diff(now, 'day')

    if (daysLeft < 0) {
        return { status: 'expired', daysLeft, isWarning: true }
    } else if (daysLeft <= 3) {
        return { status: 'expiring', daysLeft, isWarning: true }
    }

    return { status: 'active', daysLeft, isWarning: false }
}

const getSafePath = (
    template: string | undefined,
    params: Record<string, string>
) => {
    if (!template) return null

    try {
        return generatePath(template, params)
    } catch {
        return null
    }
}

// Get status styling
const getStatusStyle = (status: string | undefined) => {
    switch (status) {
        case ServiceStatus.Active:
            return {
                bg: 'bg-emerald-50',
                border: 'border-emerald-200',
                text: 'text-emerald-700',
                badge: 'bg-emerald-100 text-emerald-700',
            }
        case ServiceStatus.Trialing:
            return {
                bg: 'bg-amber-50',
                border: 'border-amber-200',
                text: 'text-amber-700',
                badge: 'bg-amber-100 text-amber-700',
            }
        case ServiceStatus.WaitingConfirm:
            return {
                bg: 'bg-blue-50',
                border: 'border-blue-200',
                text: 'text-blue-700',
                badge: 'bg-blue-100 text-blue-700',
            }
        case ServiceStatus.Cancelled:
        case ServiceStatus.Rejected:
            return {
                bg: 'bg-red-50',
                border: 'border-red-200',
                text: 'text-red-700',
                badge: 'bg-red-100 text-red-700',
            }
        case ServiceStatus.PendingTerminated:
            return {
                bg: 'bg-orange-50',
                border: 'border-orange-200',
                text: 'text-orange-700',
                badge: 'bg-orange-100 text-orange-700',
            }
        case ServiceStatus.Resubmit:
            return {
                bg: 'bg-yellow-50',
                border: 'border-yellow-200',
                text: 'text-yellow-700',
                badge: 'bg-yellow-100 text-yellow-700',
            }
        default:
            return {
                bg: 'bg-gray-50',
                border: 'border-gray-200',
                text: 'text-gray-700',
                badge: 'bg-gray-100 text-gray-700',
            }
    }
}

const POCServiceCard: React.FC<POCServiceCardProps> = ({
    note,
    isLocked,
    isCompleted,
    onUpdateResult,
    onRetention,
    onConvert,
}) => {
    const { t } = useTranslation()
    const serviceId = note.service_poc?.service_id
    const storageKey =
        serviceId == null ? null : `poc-convert-clicked:${serviceId}`
    const [isClicked, setIsClicked] = useState(() => {
        if (!storageKey || typeof window === 'undefined') return false

        return window.sessionStorage.getItem(storageKey) === 'true'
    })

    const expiryStatus = getPocExpiryStatus(
        note.service_poc?.service_stopped_date
    )
    const statusStyle = getStatusStyle(note.service_poc?.service_status)
    const servicePath =
        serviceId == null
            ? null
            : getSafePath(paths.serviceDetail, {
                  id: String(serviceId),
              })
    const expiryStatusLabel =
        expiryStatus.status === 'expired'
            ? t('services.pocs.expired', {
                  days: Math.abs(expiryStatus.daysLeft ?? 0),
              })
            : expiryStatus.daysLeft === 0
              ? t('due-today')
              : t('services.pocs.remaining', {
                    days: expiryStatus.daysLeft,
                })
    const canShowActions =
        note.service_poc?.service_status === ServiceStatus.Trialing &&
        !isLocked &&
        !isCompleted
    const actionsDisabled = isClicked

    useEffect(() => {
        if (!storageKey || typeof window === 'undefined') return

        const storedIsClicked =
            window.sessionStorage.getItem(storageKey) === 'true'
        setIsClicked(storedIsClicked)
    }, [storageKey])

    useEffect(() => {
        if (!storageKey || typeof window === 'undefined') return

        if (note.service_poc?.service_status !== ServiceStatus.Trialing) {
            window.sessionStorage.removeItem(storageKey)
            if (isClicked) {
                setIsClicked(false)
            }
        }
    }, [isClicked, note.service_poc?.service_status, storageKey])

    return (
        <div
            className={`p-2 rounded border ${statusStyle.bg} ${statusStyle.border}`}
        >
            {/* Header with link */}
            {servicePath ? (
                <Link
                    to={servicePath}
                    target="_blank"
                    className="flex items-center gap-1 mb-1 group"
                >
                    <ServiceTrialIcons
                        className={`w-3 h-3 ${statusStyle.text}`}
                    />
                    <span
                        className={`text-xs font-medium ${statusStyle.text} group-hover:underline`}
                    >
                        {note?.service_poc?.service_id} -{' '}
                        {note.service_poc?.service_name}
                    </span>
                </Link>
            ) : (
                <div className="flex items-center gap-1 mb-1 group">
                    <ServiceTrialIcons
                        className={`w-3 h-3 ${statusStyle.text}`}
                    />
                    <span className={`text-xs font-medium ${statusStyle.text}`}>
                        {note?.service_poc?.service_id} -{' '}
                        {note.service_poc?.service_name}
                    </span>
                </div>
            )}

            {/* Status & Expiry Info */}
            <div className="flex items-center gap-2 text-[10px]">
                <span
                    className={`px-1.5 py-0.5 rounded font-medium ${statusStyle.badge}`}
                >
                    {t(
                        `services.${note.service_poc?.service_status || 'pending'}`
                    )}
                </span>
                {expiryStatus.daysLeft !== null && (
                    <>
                        <span className="text-gray-400">•</span>
                        <span
                            className={
                                expiryStatus.isWarning
                                    ? 'text-red-600 font-medium'
                                    : 'text-gray-500'
                            }
                        >
                            {expiryStatusLabel}
                        </span>
                    </>
                )}
            </div>

            {/* Date range */}
            {(note.service_poc?.service_start_date ||
                note.service_poc?.service_stopped_date) && (
                <div className="mt-1 text-[10px] text-gray-500">
                    {note.service_poc?.service_start_date &&
                        formatDateWithFormatter(
                            note.service_poc.service_start_date,
                            'DD/MM/YYYY'
                        )}
                    {note.service_poc?.service_start_date &&
                        note.service_poc?.service_stopped_date &&
                        ' → '}
                    {note.service_poc?.service_stopped_date &&
                        formatDateWithFormatter(
                            note.service_poc.service_stopped_date,
                            'DD/MM/YYYY'
                        )}
                </div>
            )}

            {/* Actions */}
            {canShowActions && (
                <div className="flex gap-1 mt-2">
                    <button
                        className={`flex-1 px-1.5 py-1 text-[10px] rounded border ${
                            actionsDisabled
                                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                        }`}
                        onClick={() => !actionsDisabled && onUpdateResult(note)}
                        disabled={actionsDisabled}
                    >
                        {t('update-result')}
                    </button>
                    <button
                        className={`flex-1 px-1.5 py-1 text-[10px] rounded border ${
                            actionsDisabled
                                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                        }`}
                        onClick={() => !actionsDisabled && onRetention(note)}
                        disabled={actionsDisabled}
                    >
                        {t('extend')}
                    </button>
                    <button
                        className={`flex-1 px-1.5 py-1 text-[10px] rounded border ${
                            actionsDisabled
                                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                        }`}
                        onClick={() => {
                            if (actionsDisabled) return
                            if (storageKey && typeof window !== 'undefined') {
                                window.sessionStorage.setItem(
                                    storageKey,
                                    'true'
                                )
                            }
                            setIsClicked(true)
                            onConvert(note)
                        }}
                        disabled={actionsDisabled}
                    >
                        {t('convert-poc-to-service')}
                    </button>
                </div>
            )}
        </div>
    )
}

export default POCServiceCard
